import { SlideOverContentWrapper, SlideOverFooter, SlideOverWrapper } from '@/components/slideOvers_v2';
import { Loadable } from '@/components/loaders';
import { DataSourceTypePanelHeaderV2 } from '@/features/dataSources/components';
import {
  useSourceConnectorAuth,
  useSourceConnectorAuthConfig,
} from '@/features/dataSources/components/sourceConnectors/useSourceConnectorAuth';
import { SourceConnectorCatalogItem } from '@/features/dataSources/components/sourceConnectors/useSourceConnectorCatalogItems';
import { DataSourceIDEnum } from '@/models/dataSource';
import { ISlideOverContentProps } from '@/models/slideOver';
import Form, { IChangeEvent } from '@rjsf/core';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { FC, FormEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import '@/features/destinations/components/customConnectors/CustomConnectorForm.css';

type SourceConnectorAuthSlideOverProps = ISlideOverContentProps & {
  item: SourceConnectorCatalogItem;
};

export const SourceConnectorAuthSlideOver: FC<SourceConnectorAuthSlideOverProps> = ({ item, closeSlideOver }) => {
  const { t } = useTranslation('data_sources');
  const formRef = useRef<Form>(null);
  const { connector, dataSourceType, isPlaceholder } = item;
  const typeId = dataSourceType.id as DataSourceIDEnum;
  const { schema, uiSchema } = useSourceConnectorAuthConfig(connector);
  const { submitAuth, isLoading } = useSourceConnectorAuth(connector);

  const handleConnect = async () => {
    formRef.current?.submit();
  };

  const handleSubmit = async (data: IChangeEvent<any, RJSFSchema, any>, event: FormEvent<any>) => {
    const success = await submitAuth(data, event);
    if (success) closeSlideOver();
  };

  return (
    <SlideOverWrapper>
      <DataSourceTypePanelHeaderV2 id={typeId} title={t(`catalog.${typeId}.title`)} />

      <SlideOverContentWrapper className='!mt-0 gap-5'>
        <div className='flex flex-col gap-2 rounded-xl border border-brand-100 bg-brand-50 p-4'>
          <p className='w-fit text-sm font-medium text-brand-600 underline'>
            {t('source_connectors.what_you_need')}
          </p>
        </div>

        <Loadable isLoading={isLoading}>
          <div className={twMerge('custom-connector-form', isPlaceholder && 'pointer-events-none opacity-60')}>
            {schema ? (
              <Form ref={formRef} schema={schema} uiSchema={uiSchema} validator={validator} onSubmit={handleSubmit}>
                <span />
              </Form>
            ) : (
              <p className='text-sm text-secondary-600'>{t('source_connectors.form_unavailable')}</p>
            )}
          </div>
        </Loadable>

        <p className='text-xs text-secondary-500'>{t('source_connectors.credentials_encrypted_note')}</p>
      </SlideOverContentWrapper>

      <SlideOverFooter
        closeSlideOver={closeSlideOver}
        confirmLabel={t('source_connectors.connect')}
        onConfirmClicked={isPlaceholder ? () => {} : handleConnect}
        showCancelButton={false}
      />
    </SlideOverWrapper>
  );
};
